<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import { setProductRoute } from '~/utils/constants'

	const { pricingCategories } = usePricingList()
	const activeTab = ref('health')
	const { useSelector } = useElementSelector('#card-parents', (el: HTMLElement) => {
		const elementChildren = Array.from(el.children)
		const targetHeight = elementChildren[0]?.getBoundingClientRect().height ?? 700
		requestAnimationFrame(() => {
			el.style.maxHeight = `${targetHeight}px`
		})
	})
	const { currencyToNaira } = useCurrency()

	const { toLower } = StringMethods()
	const { allProduct: allProductsRef } = useProductLogic('business')

	const productCollectionMap = computed(() => {
		const map: Record<string, any[]> = {}
		pricingCategories.value.forEach(categoryItem => {
			const category = toLower(categoryItem.key)
			const list = filterProducts(allProductsRef.value, false, true).filter(
				p => p?.code_name !== ProductCode.LAPTOP_COVER && toLower(p.productCategory.name) === category
			)

			if (toLower(category) === 'health') {
				map[categoryItem.key] = list.map(product => {
					const originalQuarterlyPrice = product.prices.find(p => toLower(p.cover_period) === 'quarterly')

					if (originalQuarterlyPrice) {
						const monthCount = Number(originalQuarterlyPrice.month ?? 3)
						const totalMarketPrice = Number(originalQuarterlyPrice.market_price) * monthCount

						const updatedQuarterlyPrice = {
							...originalQuarterlyPrice,
							market_price: totalMarketPrice
						}

						return {
							...product,
							prices: [updatedQuarterlyPrice],
							price: updatedQuarterlyPrice,
							pricingBenefits: product.pricingBenefits
								? {
										...product.pricingBenefits,
										price: product.pricingBenefits.price
											? {
													...product.pricingBenefits.price,
													text: updatedQuarterlyPrice.market_price.toString()
												}
											: product.pricingBenefits.price,
										description: {
											text: `For as low as <span class=\"inline-flex sm:text-[2em] text-[1.5em] font-[700]\">${currencyToNaira(updatedQuarterlyPrice.market_price)}</span> / staff ${updatedQuarterlyPrice.cover_period}`
										}
									}
								: product.pricingBenefits,
							cover_period: 'Quarterly'
						}
					}
					return product
				})
			} else {
				map[categoryItem.key] = list
			}
		})
		return map
	})

	const scrollToView = (e: string) => {
		const key = String(e).toLowerCase()
		const wrapperEl = useSelector()
		const wrapperKids = Array.from(wrapperEl?.children || [])

		if (Array.isArray(wrapperKids) && wrapperEl) {
			const index = pricingCategories.value.findIndex(item => item.key === key)

			if (index !== -1 && wrapperKids[index]) {
				const card = wrapperKids[index]
				const targetHeight = card.getBoundingClientRect().height

				requestAnimationFrame(() => {
					wrapperEl.style.maxHeight = `${targetHeight}px`

					const wrapperRect = wrapperEl.getBoundingClientRect()
					const cardRect = card.getBoundingClientRect()

					// Calculate the scroll position to center the card vertically within the wrapper
					const scrollTop =
						cardRect.top - wrapperRect.top + wrapperEl.scrollTop - wrapperRect.height / 2 + cardRect.height / 2

					// Scroll the wrapper to the calculated position
					wrapperEl.scrollTo({
						top: scrollTop,
						behavior: 'smooth'
					})
				})
			}
		}
	}
</script>

<template>
	<section class="relative md:min-h-[1000px]" aria-labelledby="pricing-title">
		<div class="sticky top-0 left-0 z-10" ref="pricingWrapper">
			<ui-title-tag tag="Our Products" />
			<ui-title-header
				id="pricing-title"
				title="Flexible insurance products to meet your needs"
				:ui="{
					size: 'text-[2rem]/[2.4rem] md:text-[2.625rem]/[3.15rem] lg:text-[3.25rem]/[3.9rem]'
				}"
				class="max-w-[776px] mx-auto text-center px-5"
			/>

			<div class="w-full z-40 dark:bg-gray-950 bg-current mt-10">
				<tools-tab
					v-model:active-tab="activeTab"
					:tabs="pricingCategories"
					class="max-w-[792px] mx-auto"
					:ui="{
						line: { default: 'hidden' },
						tab: {
							default: 'border font-semibold xs:text-sm text-xs rounded-[40px]',
							height: 'xs:h-10 sm:h-[56px]',
							padding: 'px-3 py-5',
							width: 'w-full',
							body: {
								border: 'border-0 w-full',
								alignItem: 'justify-center',
								padding: 'px-5'
							},
							inactive: {
								color: 'text-main',
								default: 'border-main'
							},
							active: {
								color: 'text-white',
								default: 'bg-main'
							}
						}
					}"
					@selected="scrollToView"
				/>

				<div
					class="my-16 py-16 sm:py-20 mx-auto bg-main overflow-hidde sm:px-5 px-3 md:px-10 invisible-scrollbar space-y-24 h-full"
				>
					<div
						class="w-full max-w-[1240px] xl:max-w-[1500px] mx-auto snap-y snap-mandatory invisible-scrollbar gap-y-2 overflow-hidden relative my-auto"
						id="card-parents"
					>
						<ui-carousel
							v-for="category in pricingCategories"
							:key="'product-pricing-' + category.key"
							:data-id="`carousel-${category.key}`"
							:data-category="category.key"
							:ui="{
								wrapper: { default: 'py-5 xl:justify-center', gap: 'gap-x-4' },
								default: 'sm:pt-1 sm:first:pb-2 custom-transition-3 snap-center'
							}"
						>
							<template v-for="(product, pi) in productCollectionMap[category.key]" :key="`price-${pi}`">
								<ui-pricing-card
									v-bind="product.pricingBenefits"
									:name="{ text: product.alternate_name }"
									individual-tag=""
									:ui="{
										heading: { paddingBlock: 'pb-6' },
										list: {
											items: 'mt-3 flex gap-x-3 items-center',
											size: 'text-base',
											text: 'text-[#344054]'
										},
										wrapper: {
											width: 'max-w-[320px] md:max-w-[380px]',
											hover: 'lg:hover:bg-purple-50 lg:hover:border-purple-300'
										}
									}"
									class="flex-1 mx-auto md:mx-0 snap-center card"
								>
									<template #list-icon="{ benefit }">
										<tools-icon
											v-if="!benefit.notAllowed"
											dir="fill"
											width="14"
											height="14"
											icon="check"
											path-class="group-hover:fill-base-primary fill-base-primary sm:fill-gray-400"
										/>
										<tools-icon
											v-else
											dir="fill"
											width="14"
											height="14"
											icon="not-allowed"
											path-class="group-hover:fill-error-500"
											svg-class="h-[11.67px]"
										/>
									</template>
									<template #btn-group="{ btnGroupClass }">
										<nuxt-link
											:to="setProductRoute(product.group_id ?? '', product?.code_name ?? 0)"
											:class="btnGroupClass"
											:aria-label="`open ${product.form_name}`"
										>
											Discover more
											<span class="custom-transition-3 group-hover/item:translate-x-2">
												<tools-icon
													dir="arrows"
													icon="arrow-short-r"
													:height="16"
													:width="16"
													path-class="fill-main sm:fill-gray-700 group-hover/item:fill-main stroke-[1px]"
												/>
											</span>
										</nuxt-link>
									</template>
								</ui-pricing-card>
							</template>
						</ui-carousel>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
